import type { SBStatusEvent } from '@avisoma/shared';

/**
 * DR-3307 (guide Rule 7 — "older revision still OPEN"): the server-side revision
 * fold (`collapseRevisionLanes`) keeps the highest revision of an SB as the live
 * entry and moves every older revision into the project-level
 * `supersededRevisions` history, each linked back to its survivor through
 * `supersededBy`. When the surviving entry reads not-applicable but a folded
 * older revision still prints *open* with forward-looking due data, the fold has
 * hidden a live obligation behind an N/A headline — the reader must go and
 * confirm which revision governs.
 *
 * Scope (verified by executable repro, DR-3307 review): the server folds only
 * within one applicability lane — a revision with an INCOMPATIBLE part/serial
 * identity (e.g. 72-0579's component-level Rev.01 vs the engine-level Rev.05)
 * never enters `supersededRevisions` at all; it stays its own live open entry,
 * fully visible in the list, so nothing is hidden and no warning is needed.
 * This advisory covers the same-lane fold, the one case where an open older
 * revision with due data IS removed from the live list.
 *
 * Pure presentation-layer derivation: everything below comes from the payload the
 * project view already receives, so no backend/webhook/shared change is involved.
 */

/** A superseded-history row as the project payload delivers it (see `ProjectDataSBEvent`). */
export type SupersededSBRevisionRow = SBStatusEvent & {
  supersededBy?: { sbNumber?: string; revision?: string };
};

/** Casing/whitespace-insensitive comparison token; absent, blank, and non-string collapse to ''. */
function matchToken(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim().replace(/\s+/g, ' ').toUpperCase();
}

/**
 * Does this history row's `supersededBy` link name exactly this entry? The link is
 * stamped with the survivor's FINAL display values (`resolveSupersededLinks`), and
 * its `revision` key is omitted when the survivor carries none — so an absent link
 * revision must match an absent entry revision, and both compare as ''.
 */
function linksToEntry(row: SupersededSBRevisionRow, entry: SBStatusEvent): boolean {
  const link = row.supersededBy;
  if (!link?.sbNumber) return false;
  return (
    matchToken(link.sbNumber) === matchToken(entry.sbNumber) &&
    matchToken(link.revision) === matchToken(entry.revision) &&
    identityCompatible(row, entry)
  );
}

/**
 * The `supersededBy` link carries only SB number + revision, so when two
 * applicability lanes share both (per-serial repeats of the same SB), the link
 * alone would attribute one lane's folded history to EVERY survivor lane. The
 * server folds only within one applicability lane, so a history row and its
 * true survivor always have compatible part/serial identity — require the same
 * here. An absent value on either side cannot disprove compatibility and passes.
 * DR-5720: the operator marker (`requirement`, TRAX `MW325` / `MW326`) splits the fold
 * too, so it must be EQUAL — absent and blank compare as '', and an absent marker
 * does not match `MW326`.
 */
function identityCompatible(row: SBStatusEvent, entry: SBStatusEvent): boolean {
  const differs = (a: string | undefined, b: string | undefined): boolean => {
    const ta = matchToken(a);
    const tb = matchToken(b);
    return ta !== '' && tb !== '' && ta !== tb;
  };
  return (
    matchToken(row.requirement) === matchToken(entry.requirement) &&
    !differs(row.partNumber, entry.partNumber) &&
    !differs(row.serialNumber, entry.serialNumber)
  );
}

/**
 * Does the row carry live forward-looking due data — i.e. is it still asking for
 * work? Any one of the next-due thresholds, or a printed count to go (DR-6111), is enough.
 * A closed historical revision normally carries only compliance* anchors, so it never trips this.
 */
function hasForwardLookingDue(row: SBStatusEvent): boolean {
  const nonBlank = (value: unknown): boolean => typeof value === 'string' && value.trim() !== '';
  return (
    nonBlank(row.nextDueDate) ||
    Number.isFinite(row.nextDueCycles) ||
    Number.isFinite(row.nextDueHours) ||
    Number.isFinite(row.remainingCycles) ||
    Number.isFinite(row.remainingHours) ||
    nonBlank(row.nextDueShopVisit) ||
    row.nextDueAtShopVisit === true
  );
}

/**
 * The folded older revisions of `entry` that still print open with live due data.
 * Empty unless the surviving entry itself is not-applicable — an entry that is
 * open or otherwise live already tells the reader there is work outstanding, so an
 * ordinary revision chain (the 72-1033 pattern) must stay unflagged.
 *
 * Note on the open family: the shared `status` enum has no `repetitive` member —
 * a recurring SB is `open` with a `repeatInterval*` — so `open` is the whole family.
 */
export function findLiveOpenSupersededRevisions<T extends SupersededSBRevisionRow>(
  entry: SBStatusEvent,
  supersededRevisions: T[] | undefined,
): T[] {
  if (entry.status !== 'not-applicable' || !supersededRevisions?.length) return [];
  return supersededRevisions.filter(
    (row) => row.status === 'open' && hasForwardLookingDue(row) && linksToEntry(row, entry),
  );
}
